import React, { useState, useMemo } from "react";
import {
  Link2, Plus, ExternalLink, AlertTriangle, CheckCircle2,
  ShieldCheck, Filter, Search, X, Trash2, Globe
} from "lucide-react";
import { SeoMarket, BacklinkItem } from "./types";

interface SeoBacklinksTabProps {
  market: SeoMarket;
  backlinksList: BacklinkItem[];
  setBacklinksList: React.Dispatch<React.SetStateAction<BacklinkItem[]>>;
}

export default function SeoBacklinksTab({
  market,
  backlinksList,
  setBacklinksList
}: SeoBacklinksTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Backlink Form
  const [targetPath, setTargetPath] = useState("/samla-lan");
  const [anchorText, setAnchorText] = useState("");
  const [anchorType, setAnchorType] = useState<BacklinkItem["anchorType"]>("brand");
  const [referringDomain, setReferringDomain] = useState("");
  const [domainRating, setDomainRating] = useState<number>(50);
  const [linkType, setLinkType] = useState<BacklinkItem["linkType"]>("guest_post");
  const [costDkk, setCostDkk] = useState<number>(0);
  const [status, setStatus] = useState<BacklinkItem["status"]>("in_progress");
  const [liveUrl, setLiveUrl] = useState("");

  const marketBacklinks = backlinksList.filter(b => b.market === market);

  // Anchor text ratio calculation for current market
  const anchorRatios = useMemo(() => {
    const total = marketBacklinks.length || 1;
    const brand = marketBacklinks.filter(b => b.anchorType === "brand").length;
    const partial = marketBacklinks.filter(b => b.anchorType === "partial_match").length;
    const exact = marketBacklinks.filter(b => b.anchorType === "exact_match").length;
    return {
      brandPct: Math.round((brand / total) * 100),
      partialPct: Math.round((partial / total) * 100),
      exactPct: Math.round((exact / total) * 100),
      exactWarning: (exact / total) > 0.25 // Google Penguin warning if exact match > 25%
    };
  }, [marketBacklinks]);

  const filteredList = marketBacklinks
    .filter(b => filterStatus === "all" || b.status === filterStatus)
    .filter(b => 
      !searchQuery || 
      b.anchorText.toLowerCase().includes(searchQuery.toLowerCase()) || 
      b.referringDomain.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.targetPath.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const handleAddBacklink = (e: React.FormEvent) => {
    e.preventDefault();
    if (!referringDomain.trim() || !anchorText.trim()) return;

    const newItem: BacklinkItem = {
      id: `bl-${Date.now()}`,
      market,
      targetPath: targetPath.trim() || "/",
      anchorText: anchorText.trim(),
      anchorType,
      referringDomain: referringDomain.trim().toLowerCase().replace(/^https?:\/\//, ""),
      domainRating: Number(domainRating) || 30,
      linkType,
      costDkk: Number(costDkk) || 0,
      status,
      dateAcquired: new Date().toISOString().split("T")[0],
      lastCheckedDate: new Date().toISOString().split("T")[0],
      liveUrl: liveUrl.trim() || undefined
    };

    setBacklinksList(prev => [newItem, ...prev]);
    setIsAddModalOpen(false);

    // Reset
    setAnchorText("");
    setReferringDomain("");
    setLiveUrl("");
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Vil du slette dette backlink fra oversigten?")) {
      setBacklinksList(prev => prev.filter(b => b.id !== id));
    }
  };

  const handleStatusChange = (id: string, newStatus: BacklinkItem["status"]) => {
    setBacklinksList(prev => prev.map(b => b.id === id ? { ...b, status: newStatus } : b));
  };

  return (
    <div className="space-y-6">

      {/* Anchor Text Diversity Meter (Anti-Penguin Protection) */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#0F3F34]" />
              <span>Ankertekst-Fordeling & Anti-Penguin Sikkerhed ({market})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Google straffer sider hvor ankerteksterne er for unaturlige eller overoptimerede med Exact Match (&gt; 25%).
            </p>
          </div>

          {anchorRatios.exactWarning ? (
            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-red-100 text-red-800 flex items-center gap-1.5 shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span>For mange exact match links!</span>
            </span>
          ) : (
            <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1.5 shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Sund ankertekst profil</span>
            </span>
          )}
        </div>

        {/* Multi-segment Progress Bar */}
        <div className="h-3.5 w-full rounded-full bg-gray-100 overflow-hidden flex">
          <div 
            style={{ width: `${anchorRatios.brandPct}%` }} 
            className="bg-[#0F3F34] transition-all"
            title={`Brand: ${anchorRatios.brandPct}%`}
          />
          <div 
            style={{ width: `${anchorRatios.partialPct}%` }} 
            className="bg-emerald-500 transition-all"
            title={`Partial Match: ${anchorRatios.partialPct}%`}
          />
          <div 
            style={{ width: `${anchorRatios.exactPct}%` }} 
            className="bg-amber-500 transition-all"
            title={`Exact Match: ${anchorRatios.exactPct}%`}
          />
        </div>

        <div className="flex flex-wrap gap-4 text-xs font-medium text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#0F3F34]"></span>
            <span>Brand / URL ({anchorRatios.brandPct} %)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span>Partial Match ({anchorRatios.partialPct} %)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <span>Exact Match ({anchorRatios.exactPct} %)</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
        
        <div className="flex items-center gap-2 flex-1 max-w-md bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Søg i kildedomæner, ankertekster, mål-sider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-sm bg-transparent border-0 focus:outline-none p-0 text-gray-800 placeholder-gray-400"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 font-medium text-gray-700 focus:outline-none"
          >
            <option value="all">Alle statusser ({marketBacklinks.length})</option>
            <option value="live_verified">✅ Live & Verificeret ({marketBacklinks.filter(b => b.status === "live_verified").length})</option>
            <option value="in_progress">⏳ Under udarbejdelse ({marketBacklinks.filter(b => b.status === "in_progress").length})</option>
            <option value="pitched">✉️ Tilbudt ({marketBacklinks.filter(b => b.status === "pitched").length})</option>
            <option value="lost">❌ Tabt ({marketBacklinks.filter(b => b.status === "lost").length})</option>
          </select>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white text-xs font-bold rounded-xl transition shadow-xs"
          >
            <Plus className="w-4 h-4" />
            <span>Nyt link</span>
          </button>
        </div>
      </div>

      {/* Backlinks Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50/80 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="py-3.5 px-5">Mål-Side på SimplyLoan</th>
                <th className="py-3.5 px-4">Ankertekst & Type</th>
                <th className="py-3.5 px-4">Kilde Domæne & DR</th>
                <th className="py-3.5 px-4">Kategori</th>
                <th className="py-3.5 px-4">Pris (DKK)</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-5 text-right">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    Ingen backlinks registreret for {market}.
                  </td>
                </tr>
              ) : (
                filteredList.map((link) => (
                  <tr key={link.id} className="hover:bg-gray-50/60 transition-colors">
                    
                    <td className="py-4 px-5">
                      <span className="font-mono font-bold text-xs text-[#0F3F34] bg-[#0F3F34]/10 px-2 py-0.5 rounded">
                        {link.targetPath}
                      </span>
                    </td>

                    <td className="py-4 px-4">
                      <div className="font-bold text-gray-900 leading-snug">{link.anchorText}</div>
                      <span className="text-[10px] uppercase font-bold text-gray-400">
                        {link.anchorType.replace('_', ' ')}
                      </span>
                    </td>

                    <td className="py-4 px-4">
                      <div className="font-semibold text-gray-800">{link.referringDomain}</div>
                      <span className="text-xs text-indigo-600 font-bold">DR {link.domainRating}</span>
                    </td>

                    <td className="py-4 px-4 text-xs capitalize text-gray-600">
                      {link.linkType.replace('_', ' ')}
                    </td>

                    <td className="py-4 px-4 text-xs font-semibold text-gray-900">
                      {link.costDkk > 0 ? `${link.costDkk.toLocaleString('da-DK')} kr.` : 'Gratis (Partner)'}
                    </td>

                    <td className="py-4 px-4">
                      <select
                        value={link.status}
                        onChange={(e) => handleStatusChange(link.id, e.target.value as BacklinkItem["status"])}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1 border transition focus:outline-none ${
                          link.status === "live_verified" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                          link.status === "in_progress" ? "bg-amber-50 text-amber-800 border-amber-200" :
                          link.status === "pitched" ? "bg-blue-50 text-blue-800 border-blue-200" :
                          "bg-red-50 text-red-800 border-red-200"
                        }`}
                      >
                        <option value="pitched">✉️ Tilbudt</option>
                        <option value="in_progress">⏳ Undervejs</option>
                        <option value="live_verified">✅ Live & Verificeret</option>
                        <option value="lost">❌ Tabt</option>
                      </select>
                    </td>

                    <td className="py-4 px-5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {link.liveUrl && (
                          <a
                            href={link.liveUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition"
                            title="Se live placering"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(link.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Slet link"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Tilføj Backlink */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#0F3F34]" />
                <span>Registrér nyt Backlink ({market})</span>
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddBacklink} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Kilde Domæne *</label>
                <input
                  type="text"
                  required
                  placeholder="f.eks. finansnyt-sverige.se"
                  value={referringDomain}
                  onChange={(e) => setReferringDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Ankertekst *</label>
                  <input
                    type="text"
                    required
                    placeholder="f.eks. SimplyLoan"
                    value={anchorText}
                    onChange={(e) => setAnchorText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Ankertype</label>
                  <select
                    value={anchorType}
                    onChange={(e) => setAnchorType(e.target.value as BacklinkItem["anchorType"])}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  >
                    <option value="brand">Brand / Navn</option>
                    <option value="partial_match">Partial Match</option>
                    <option value="exact_match">Exact Match</option>
                    <option value="generic">Generisk (klik her)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Målside Path</label>
                  <input
                    type="text"
                    value={targetPath}
                    onChange={(e) => setTargetPath(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Est. Domain Rating (DR)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={domainRating}
                    onChange={(e) => setDomainRating(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Type</label>
                  <select
                    value={linkType}
                    onChange={(e) => setLinkType(e.target.value as BacklinkItem["linkType"])}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  >
                    <option value="guest_post">Gæsteindlæg</option>
                    <option value="niche_edit">Niche Edit</option>
                    <option value="pr_partner">PR / Partner</option>
                    <option value="broker">Finans Affiliate</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Pris (DKK)</label>
                  <input
                    type="number"
                    value={costDkk}
                    onChange={(e) => setCostDkk(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Live URL (hvis publiceret)</label>
                <input
                  type="url"
                  placeholder="https://domæne.se/artikel"
                  value={liveUrl}
                  onChange={(e) => setLiveUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl transition"
                >
                  Gem backlink
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
